import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import { AbsoluteLoader } from './styles';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { Button } from '@kizen/kds/Button';

type ModalProps = {
  onClose: (state?: Record<string, any>) => void;
  config: MarketplacePlugin;
  children: any;
  disabled?: boolean;
  schemaImportStep?: boolean;
  onContinueImport?: () => void;
  continueImportDisabled?: boolean;
};

export const SetupAssistantModal = ({
  onClose,
  config,
  children,
  disabled,
  schemaImportStep,
  onContinueImport,
  continueImportDisabled,
}: ModalProps) => {
  const { t } = useTranslation();

  const {
    inferencePending,
    state,
    expressionsIdle,
    validateForm,
    initialExpressionsPending,
    waitingExpressionCount,
    flattenedFieldsByKey,
  } = useSetupAssistant();

  const showExpressionLoader = waitingExpressionCount > 0;

  const showBlockingLoader =
    (inferencePending || !expressionsIdle || initialExpressionsPending) &&
    !schemaImportStep;

  return (
    <BasicModal
      show
      onHide={() => onClose()}
      onConfirm={async () => {
        const { isValid, includedKeys } = await validateForm();

        if (isValid) {
          const result: Record<string, any> = {};

          includedKeys.forEach((key: string) => {
            let value: any = state[key];

            const config = flattenedFieldsByKey[key];

            if (!value) {
              value = {
                key,
                type: config.type,
                config,
                value: config.allow_multiple ? [] : undefined,
              };
            }

            value.config = config;

            result[key] = value;
          });

          onClose(result);
        }
      }}
      size={schemaImportStep ? 'medium' : 'large'}
      heading={t('{{pluginName}} Configuration', {
        pluginName: config.name,
      })}
      modalBodyProps={{
        scrollPaddingTop: '40px',
      }}
      disabled={disabled || schemaImportStep}
      hideFooter={schemaImportStep && !onContinueImport}
      {...(onContinueImport
        ? {
            actionButton: false,
            leftBtn: null,
            rightBtn: (
              <Button
                disabled={continueImportDisabled}
                qa={{ id: 'plugin-schema-import-continue' }}
              >
                {t('Continue')}
              </Button>
            ),
            rightBtnClickHandler: onContinueImport,
          }
        : {})}
    >
      {showBlockingLoader ? (
        <div className="absolute left-0 right-0 top-0 bottom-0">
          <AbsoluteLoader loading />
        </div>
      ) : null}
      {showExpressionLoader && !showBlockingLoader ? (
        <div className="absolute left-0 right-0 top-0 bottom-0">
          <AbsoluteLoader loading transparent />
        </div>
      ) : null}
      {children}
    </BasicModal>
  );
};
